import React from "react"
import {
  Box, ListItem, Text, UnorderedList, VStack,
} from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import { ValidationError } from "components/BadgeBuilder/utils/badgeValidation"

type Props = {
  isOpen: boolean
  onClose: () => void
  onSaveWithWarnings: () => void
  onCancel: () => void
  issues: ValidationError[]
}

const IssueList = ({ issues }: { issues: ValidationError[] }) => (
  <UnorderedList spacing={1}>
    {issues.map((issue) => (
      <ListItem key={`${issue.elementId}-${issue.fieldName ?? issue.type}`}>
        <Text as="span" fontWeight="semibold">{issue.elementLabel}</Text>
        {`: ${issue.message}`}
        {issue.currentColor && (
          <Box
            as="span"
            display="inline-block"
            w={3}
            h={3}
            ml={2}
            verticalAlign="middle"
            borderRadius="full"
            bg={issue.currentColor}
            borderWidth={1}
            borderColor="gray.300"
          />
        )}
      </ListItem>
    ))}
  </UnorderedList>
)

/**
 * Shown on save when the template has issues that may spoil the print. It can be saved anyway,
 * unless an image element has no image.
 */
const isBlocking = (issue: ValidationError) => issue.type === "empty_image"

const ValidationWarningModal = ({
  isOpen,
  onClose,
  onSaveWithWarnings,
  onCancel,
  issues,
}: Props) => {
  // An image element without an image stops the save: the mobile app can't load such a badge
  const blocking = issues.some(isBlocking)

  const handleSaveWithWarnings = () => {
    onSaveWithWarnings()
    onClose()
  }

  return (
    <Modal
      show={isOpen}
      hide={onClose}
      size="lg"
      title={blocking ? "Fix this before saving" : "Check these before saving"}
      actions={blocking
        ? [<Button key="back" onClick={onCancel}>Back to Editor</Button>]
        : [
          <Button key="cancel" variant="outline" colorScheme="button.light" onClick={onCancel}>
            Cancel
          </Button>,
          <Button key="save" onClick={handleSaveWithWarnings}>Save Anyway</Button>,
        ]}
    >
      <VStack align="stretch" spacing={2}>
        <Text>
          {blocking
            ? "Upload an image for each image element, or delete it, to save the badge."
            : "These may not print as you expect."}
        </Text>
        <IssueList issues={issues} />
      </VStack>
    </Modal>
  )
}

export default ValidationWarningModal
